import React, { useCallback, useState } from "react";
import { ScreenStyles } from "./LocationScreen";
import { useJourney } from "./JourneyProvider";
import { validateDocument } from "../../services/allowService";
import documentImage from "../../assets/document-image.jpg";
import documentImageCDT from "../../assets/document-image-cdt.jpg";
interface DocumentScreenProps {
  styles: ScreenStyles;
  onNext?: (result?: any) => void;
}

export default function DocumentScreen({
  styles,
  onNext,
}: DocumentScreenProps) {
  const { cpf, ip, journey, setDocumentImage } = useJourney();
  const [selection, setSelection] = useState<"physical" | "digital">(
    "physical"
  );
  const [isLoading, setIsLoading] = useState(false);

  const loadSdk = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        if ((window as any).SerasaExperianIDF) return resolve();
        const script = document.createElement("script");
        script.src =
          "https://uat-idf-sdk.serasaexperian.com.br/v2/serasa-experian-idf.min.js";
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Falha ao carregar o SDK"));
        document.body.appendChild(script);
      }),
    []
  );

  const acceptedDocs =
    selection === "physical"
      ? ["RG", "CNH", "DNI", "CIN", "RNE", "RNM", "OUTROS"]
      : ["CNHDIG", "OUTROSDIG"];

  function getSdkScreenEl(): HTMLElement | null {
    const allowmeEl = document.querySelector("allowme") as
      | (HTMLElement & { shadowRoot?: ShadowRoot })
      | null;
    if (!allowmeEl) return null;
    if (allowmeEl.shadowRoot) {
      return (
        (allowmeEl.shadowRoot.getElementById("screen") as HTMLElement | null) ||
        (allowmeEl.shadowRoot.querySelector("#screen") as HTMLElement | null)
      );
    }
    return (
      (allowmeEl.querySelector("#screen") as HTMLElement) ||
      document.querySelector("allowme #screen")
    );
  }

  const injectSdkStyles = () => {
    let styleTag = document.querySelector(
      'style[data-sdk-bg="true"]'
    ) as HTMLStyleElement | null;
    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.setAttribute("data-sdk-bg", "true");
      document.head.appendChild(styleTag);
    }
    styleTag.innerHTML = `
      #serasa-instruction-screen-id,
      #serasa-instruction-screen-id main#instruction-screen {
        background-color: ${styles.backgroundColor} !important;
        color: ${styles.textColor} !important;
      }
      allowme #screen {
        background-color: ${styles.backgroundColor} !important;
        color: ${styles.textColor} !important;
      }
      allowme #screen h1,
      allowme #screen h2,
      allowme #screen p,
      allowme #screen li,
      allowme #screen label,
      allowme #screen .text,
      allowme #screen .description {
        color: ${styles.textColor} !important;
      }
      allowme #screen button,
      allowme #screen button span {
        background-color: ${styles.buttonColor} !important;
        color: ${styles.buttonLabelColor} !important;
        border-color: ${styles.buttonColor} !important;
      }
    `;

    const applyInlineFix = () => {
      const screen = getSdkScreenEl();
      if (!screen) return;
      screen.style.setProperty(
        "background-color",
        styles.backgroundColor,
        "important"
      );
      screen.style.setProperty("color", styles.textColor, "important");

      const scope: ParentNode & { querySelectorAll?: any } = ((screen as any)
        .shadowRoot ?? screen) as any;
      const texts =
        scope.querySelectorAll?.("h1,h2,p,li,label,.text,.description") ?? [];
      texts.forEach((el: HTMLElement) =>
        el.style.setProperty("color", styles.textColor, "important")
      );
      const buttons = scope.querySelectorAll?.("button,button span") ?? [];
      buttons.forEach((el: HTMLElement) => {
        el.style.setProperty(
          "background-color",
          styles.buttonColor,
          "important"
        );
        el.style.setProperty("color", styles.buttonLabelColor, "important");
        el.style.setProperty("border-color", styles.buttonColor, "important");
      });
    };

    applyInlineFix();
    const rootObserver = new MutationObserver(applyInlineFix);
    rootObserver.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
    });

    const allowmeEl = document.querySelector("allowme") as any;
    let shadowObserver: MutationObserver | null = null;
    if (allowmeEl?.shadowRoot) {
      shadowObserver = new MutationObserver(applyInlineFix);
      shadowObserver.observe(allowmeEl.shadowRoot, {
        subtree: true,
        childList: true,
        attributes: true,
      });
    }

    return () => {
      rootObserver.disconnect();
      shadowObserver?.disconnect();
    };
  };

  const customOptions = {
    wizard: {
      enabled: true,
      titleColor: styles.buttonColor,
      textColor: styles.textColor,
      titleAlign: "left",
      buttonCloseEnabled: true,
      buttonTextColor: styles.buttonLabelColor,
      buttonBackgroundNormalColor: styles.buttonColor,
      buttonBackgroundHighlightColor: styles.buttonColor,
      buttonBackgroundDisabledColor: "#5e6976",
      buttonBorderRadius: "48px",
      instructionsScreen: {
        logoBackgroundColor: styles.backgroundColor,
        iconBackgroundColor: styles.buttonColor,
        itemColor: styles.textColor,
      },
    },
    loadingScreen: { title: "Carregando..." },
    documentCustomization: {
      debugMode: false,
      acceptedDocs,
      attemptsToNoValidation: 2,
      captureScreen: {
        captureBorderColor: styles.buttonColor,
        enabledAnimationTurnDocument: true,
        enabledThumbnail: true,
        manualButtonTimeout: 60,
      },
      previewScreen: {
        enabledCropImagePreview: true,
        titleWithoutPhoto: "Lado B",
        titleWithPhoto: "Lado A",
      },
    },
  };

  function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(String(r.result)); // dataURL
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  }

  const handleAdvance = async () => {
    if (isLoading) return;
    let cleanup: (() => void) | undefined;
    let styleTag: HTMLStyleElement | null = null;

    try {
      setIsLoading(true);

      await loadSdk();

      styleTag = document.createElement("style");
      styleTag.setAttribute("data-sdk-bg", "true");
      styleTag.innerHTML = `
          #serasa-instruction-screen-id,
          #serasa-instruction-screen-id main#instruction-screen {
            background-color: ${styles.backgroundColor} !important;
          }
        `;
      document.head.appendChild(styleTag);

      const sdk: any = (window as any).SerasaExperianIDF;
      await sdk.setup({
        apiKey: "kKQKWyCbCsIt8Y2d67kgCvgxUFSOgWn5EQocDKu6",
        forceGeolocationCollector: true,
      });

      await sdk.setupDocument({
        options: customOptions,
        callback: (cb: any) => console.log("setupDocument:", cb),
      });

      cleanup = injectSdkStyles();
      requestAnimationFrame(injectSdkStyles);
      setTimeout(injectSdkStyles, 150);

      const result = await sdk.startDocument();

      const blobs: Blob[] = Array.isArray(result?.images) ? result.images : [];
      const images = await Promise.all(
        blobs.slice(0, 4).map(async (b, i) => ({
          fileName: `doc${i + 1}.jpg`,
          contentType: b.type || "image/jpeg",
          base64: await blobToBase64(b),
        }))
      );
      console.log("DocumentCaptureResult:", result);

      if (result?.error)
        throw new Error(
          result.error?.detail || "Falha na captura do documento."
        );
      const event = result?.event || result?.payload;
      if (!event) throw new Error("SDK não retornou 'event' para documento.");

      const nome = journey?.nomeCliente || journey?.client?.corporateName || "";
      const cpfOnly = (cpf ?? "").replace(/\D/g, "");

      const { data } = await validateDocument({
        integrationName: "document",
        userId: cpf ?? "",
        userIp: ip || undefined,
        event,
        cpf: cpfOnly,
        name: nome,
        protocol: journey?.protocol,
        images,
      });

      if (images.length > 0) {
        setDocumentImage(images[0]);
      }

      console.log("Resposta backend (document):", data);
      onNext?.(data);
    } catch (err) {
      console.error("Erro na captura/validação de documento:", err);
      alert("Não foi possível validar o documento. Tente novamente.");
    } finally {
      setIsLoading(false);
      cleanup?.();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <p
        style={{
          color: styles.textColor,
          fontWeight: 600,
          fontSize: 18,
          marginBottom: 16,
        }}
      >
        Escolher Documento
      </p>

      <div
        onClick={() => setSelection("physical")}
        style={{
          backgroundColor: styles.backgroundColor,
          borderRadius: 20,
          padding: 16,
          marginBottom: 12,
          boxShadow:
            selection === "physical"
              ? `0 0 0 3px ${styles.textColor}`
              : "0 0 0 2px #ccc",
          display: "flex",
          gap: 16,
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <div style={{ width: 60 }}>
          <img
            src={documentImage}
            alt="documento físico"
            style={{ width: "100%" }}
          />
        </div>
        <div>
          <p
            style={{
              color: styles.textColor,
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            Documento Físico
          </p>
          <p style={{ color: styles.textColor, fontSize: 14 }}>
            CNH, RG, DNI, CIN, RNE, RNM
          </p>
        </div>
      </div>

      <div
        onClick={() => setSelection("digital")}
        style={{
          backgroundColor: styles.backgroundColor,
          borderRadius: 20,
          padding: 16,
          marginBottom: 24,
          boxShadow:
            selection === "digital"
              ? `0 0 0 3px ${styles.textColor}`
              : "0 0 0 2px #ccc",
          display: "flex",
          gap: 16,
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <div style={{ width: 60 }}>
          <img
            src={documentImageCDT}
            alt="documento digital"
            style={{ width: "100%" }}
          />
        </div>
        <div>
          <p
            style={{
              color: styles.textColor,
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            Documento Digital
          </p>
          <p style={{ color: styles.textColor, fontSize: 14 }}>
            CDT – Carteira Digital de Trânsito
          </p>
        </div>
      </div>

      <button
        onClick={handleAdvance}
        disabled={isLoading}
        style={{
          width: "100%",
          padding: "12px 0",
          backgroundColor: styles.buttonColor,
          border: "none",
          borderRadius: 30,
          color: styles.buttonLabelColor,
          fontWeight: 600,
          fontSize: 16,
          opacity: isLoading ? 0.7 : 1,
          cursor: isLoading ? "wait" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
        aria-busy={isLoading}
        aria-disabled={isLoading}
      >
        {isLoading ? (
          <>
            <svg
              width="18"
              height="18"
              viewBox="0 0 50 50"
              role="img"
              aria-label="carregando"
            >
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray="31.4 188.4"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="0.8s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
            Validando…
          </>
        ) : (
          "Avançar"
        )}
      </button>
    </div>
  );
}
